import {
    ASSOCIATED_TOKEN_PROGRAM_ID,
    TOKEN_PROGRAM_ID,
    getAssociatedTokenAddress,
    getAccount,
    createAssociatedTokenAccountInstruction,
    TokenAccountNotFoundError,
    TokenInvalidAccountOwnerError,
    TokenInvalidMintError,
    TokenInvalidOwnerError,
    AccountLayout,
} from "@solana/spl-token";
import {
    PublicKey,
    Transaction,
    Connection,
    Commitment,
    GetProgramAccountsFilter,
    clusterApiUrl,
} from "@solana/web3.js";
import { env } from "~/env.mjs";
import {
    fetchAllDigitalAssetWithTokenByOwner,
    mplTokenMetadata,
} from "@metaplex-foundation/mpl-token-metadata";
import { isSome, publicKey } from "@metaplex-foundation/umi";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";

export const USDC_MINT =
    process.env.NODE_ENV === "development"
        ? new PublicKey("4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU")
        : new PublicKey("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v");

export const USDC_DECIMAL = 1000000; // USDC on solana has 6 decimal
export const NETWORK =
    process.env.NODE_ENV === "development" ? "devnet" : "mainnet";

// based on https://stackoverflow.com/questions/70224185/how-to-transfer-custom-spl-token-by-solana-web3-js-and-solana-sol-wallet-ad
/**
 * (rewrite)Retrieve the associated token account, or create it if it doesn't exist
 *
 * @param connection               Connection to use
 * @param payer                    Payer of the transaction and initialization fees
 * @param mint                     Mint associated with the account to set or verify
 * @param owner                    Owner of the account to set or verify
 * @param sendTransaction
 * @param allowOwnerOffCurve       Allow the owner account to be a PDA (Program Derived Address)
 * @param commitment               Desired level of commitment for querying the state
 * @param programId                SPL Token program account
 * @param associatedTokenProgramId SPL Associated Token program account
 *
 * @return Address of the new associated token account
 */
export async function getOrCreateAssociatedTokenAccount(
    connection: Connection,
    payer: PublicKey,
    mint: PublicKey,
    owner: PublicKey,
    sendTransaction: (arg0: Transaction, arg1: any) => any,
    allowOwnerOffCurve = false,
    commitment?: Commitment,
    programId = TOKEN_PROGRAM_ID,
    associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID,
) {
    const associatedToken = await getAssociatedTokenAddress(
        mint,
        owner,
        allowOwnerOffCurve,
        programId,
        associatedTokenProgramId,
    );

    // This is the optimal logic, considering TX fee, client-side computation, RPC roundtrips and guaranteed idempotent.
    // Sadly we can't do this atomically.
    let account;
    try {
        account = await getAccount(
            connection,
            associatedToken,
            commitment,
            programId,
        );
    } catch (error) {
        // TokenAccountNotFoundError can be possible if the associated address has already received some lamports,
        // becoming a system account. Assuming program derived addressing is safe, this is the only case for the
        // TokenInvalidAccountOwnerError in this code path.
        if (
            error instanceof TokenAccountNotFoundError ||
            error instanceof TokenInvalidAccountOwnerError
        ) {
            // As this isn't atomic, it's possible others can create associated accounts meanwhile.
            try {
                const transaction = new Transaction().add(
                    createAssociatedTokenAccountInstruction(
                        payer,
                        associatedToken,
                        owner,
                        mint,
                        programId,
                        associatedTokenProgramId,
                    ),
                );
                const signature = await sendTransaction(
                    transaction,
                    connection,
                );

                await connection.confirmTransaction(signature, "confirmed");
            } catch (error) {
                // Ignore all errors; for now there is no API-compatible way to selectively ignore the expected
                // instruction error if the associated account exists already.
            }
            // Now this should always succeed
            account = await getAccount(
                connection,
                associatedToken,
                commitment,
                programId,
            );
        } else {
            throw error;
        }
    }
    if (!account.mint.equals(mint)) throw new TokenInvalidMintError();
    if (!account.owner.equals(owner)) throw new TokenInvalidOwnerError();
    return account;
}

/**
 * Retrieve the associated token account
 *
 * @param connection               Connection to use
 * @param payer                    Payer of the transaction and initialization fees
 * @param mint                     Mint associated with the account to set or verify
 * @param owner                    Owner of the account to set or verify
 * @param sendTransaction
 * @param allowOwnerOffCurve       Allow the owner account to be a PDA (Program Derived Address)
 * @param commitment               Desired level of commitment for querying the state
 * @param programId                SPL Token program account
 * @param associatedTokenProgramId SPL Associated Token program account
 *
 * @return Address of the new associated token account
 */
export async function getTokenAccount(
    connection: Connection,
    mint: PublicKey,
    owner: PublicKey,
    allowOwnerOffCurve = false,
    commitment?: Commitment,
    programId = TOKEN_PROGRAM_ID,
    associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID,
) {
    const associatedToken = await getAssociatedTokenAddress(
        mint,
        owner,
        allowOwnerOffCurve,
        programId,
        associatedTokenProgramId,
    );

    // This is the optimal logic, considering TX fee, client-side computation, RPC roundtrips and guaranteed idempotent.
    // Sadly we can't do this atomically.
    const account = await getAccount(
        connection,
        associatedToken,
        commitment,
        programId,
    );

    if (!account.mint.equals(mint)) throw new TokenInvalidMintError();
    if (!account.owner.equals(owner)) throw new TokenInvalidOwnerError();
    return account;
}

export async function getWalletNFTs(walletAddress: string) {
    const umi = createUmi(SOLANA_RPC_ENDPOINT).use(mplTokenMetadata());

    const tokens = await fetchAllDigitalAssetWithTokenByOwner(
        umi,
        publicKey(walletAddress),
        { tokenAmountFilter: (amount) => amount === BigInt(1) },
    );

    const nfts = tokens.map((token) => ({
        mintAddress: token.mint.publicKey,
        name: token.metadata.name.replace(/\0/g, ""), // Remove null characters
        symbol: token.metadata.symbol.replace(/\0/g, ""),
        uri: token.metadata.uri.replace(/\0/g, ""),
        collection: isSome(token.metadata.collection)
            ? token.metadata.collection.value.key
            : null,
    }));
    return nfts;
}

export async function getUserNFTs(user: {
    accounts: { providerAccountId: string }[];
}) {
    const pKey = user.accounts.at(0)?.providerAccountId;
    if (!pKey) return undefined;

    return await getWalletNFTs(pKey);
}

const network =
    process.env.NODE_ENV === "development"
        ? WalletAdapterNetwork.Devnet
        : WalletAdapterNetwork.Mainnet;

export const SOLANA_RPC_ENDPOINT =
    env.NEXT_PUBLIC_SOLANA_RPC || clusterApiUrl(network);
